export const NO_DATA_TEXT = "No data available";
export const CREATOR_CARD_NO_DATA_TEXT = "Data not available";

const CREATOR_CARD_METRIC_KEYS = new Set([
	"followers",
	"avgengagementrate",
	"avgviews",
	"localaudiencepercentage",
	"femalepercentage",
	"malepercentage",
]);

/**
 * Treat legacy zero-valued creator insights as unavailable. ScrapeCreators
 * fallback rows created before nullable metrics were introduced contain zeroes
 * for fields that the provider never supplied.
 */
export const isUnavailableCreatorCardMetric = (key: string, value: unknown): boolean => {
	const normalizedKey = key.toLowerCase().replace(/[\s_-]/g, "");
	if (!CREATOR_CARD_METRIC_KEYS.has(normalizedKey)) return false;

	if (value === null || value === undefined) return true;
	if (typeof value === "number") return value === 0;
	if (typeof value !== "string") return false;

	const normalizedValue = value.replace(/,/g, "").trim();
	if (!/^-?\d+(?:\.\d+)?%?$/.test(normalizedValue)) return false;
	return Number(normalizedValue.replace(/%$/, "")) === 0;
};

// ─── Types ────────────────────────────────────────────────────────────────────

export interface AudienceAgeBucket {
	ageRange?: string | null;
	percentage?: number | string | null;
}

export interface MergedAudienceAgeItem {
	ageRange: string;
	percentage: number;
	malePercentage: number;
	femalePercentage: number;
}

// ─── Primitive helpers ────────────────────────────────────────────────────────

/**
 * Coerces a value to a finite number, returning 0 for anything unparseable.
 */
export const toNumericPercentage = (value?: number | string | null): number => {
	if (value == null) return 0;
	// Accept both dot and comma decimal separators (e.g. "0.8" or "0,8").
	const str = typeof value === "string" ? value.replace(",", ".") : String(value);
	const parsed = Number(str);
	return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Clamps a value to [0, 100].
 */
export const normalizeDirectPercentage = (value?: number | null): number =>
	Math.max(0, Math.min(100, Number(value || 0)));

/**
 * Returns true when a metric value is present and numeric (not null/undefined/NaN).
 */
export const hasMetricValue = (value?: number | null): value is number =>
	value !== null && value !== undefined && !Number.isNaN(value);

// ─── Formatting ───────────────────────────────────────────────────────────────

/**
 * Formats a percentage value for display.
 *
 * Pass the value as-is from the API. Values already expressed as a full
 * percentage (e.g. 42.5 meaning 42.5%) are returned directly.
 *
 * Note: avgEngagementRate from the API is in the 0–1 range, so multiply
 * by 100 before passing here, or use `formatEngagementRate` instead.
 */
export const formatPercentage = (val: number | null | undefined) => {
	if (val === null || val === undefined || Number.isNaN(val)) return NO_DATA_TEXT;
	const n = Number(val);
	const abs = Math.abs(n);
	if (abs >= 1) return `${n.toFixed(1)}%`;
	return `${n.toFixed(2)}%`;
};

/**
 * Convenience wrapper for engagement rate values that arrive in the 0–1 range.
 * Equivalent to `formatPercentage(value * 100)` but makes the intent explicit
 * at the call site, eliminating the need for inline multiplication.
 */
export const formatEngagementRate = (value?: number | null): string => {
	if (!hasMetricValue(value)) return NO_DATA_TEXT;
	return formatPercentage((value as number) * 100);
};

// ─── Audience age merging ─────────────────────────────────────────────────────

/**
 * Merges raw audience age distribution buckets into a display-ready list.
 *
 * Handles two data shapes:
 *
 * 1. **Gender-split**: The API returns each age range twice — once for male,
 *    once for female — in two equal-length halves with matching age ranges.
 *    These are zipped together so each row carries both `malePercentage` and
 *    `femalePercentage` alongside the combined `percentage`.
 *
 * 2. **Aggregated**: Buckets are grouped by `ageRange` and their percentages
 *    summed. If the total lands within [99, 101] the values are re-normalized
 *    to exactly 100% to absorb floating-point drift. Totals far from 100 are
 *    left as-is to avoid inflating sparse data (e.g. a total of 0.3% → 100%).
 */
export const mergeAudienceAgeData = (audienceAgeData: AudienceAgeBucket[]): MergedAudienceAgeItem[] => {
	if (audienceAgeData.length === 0) return [];

	/**
	 * If the sum of percentages exceeds 100, subtracts the overflow from the
	 * bucket with the highest percentage. Does nothing if total <= 100.
	 */
	const adjustOverflow = (items: MergedAudienceAgeItem[]): MergedAudienceAgeItem[] => {
		const total = items.reduce((s, b) => s + b.percentage, 0);
		if (total <= 100) return items;

		const overflow = total - 100;
		const maxIndex = items.reduce((maxI, b, i, arr) => (b.percentage > arr[maxI].percentage ? i : maxI), 0);

		return items.map((b, i) => (i === maxIndex ? { ...b, percentage: Math.max(0, b.percentage - overflow) } : b));
	};

	// Group buckets by ageRange to support datasets that may contain
	// either gender-split pairs or single aggregated buckets. For each
	// ageRange:
	//  - if there are two buckets, treat them as a gender pair (preserve
	//    the order provided for male/female fields) and sum their values;
	//  - if there is one bucket, keep it as a single aggregated bucket.
	const grouped = new Map<string, AudienceAgeBucket[]>();
	audienceAgeData.forEach((b) => {
		const key = b.ageRange || NO_DATA_TEXT;
		if (!grouped.has(key)) grouped.set(key, []);
		grouped.get(key)!.push(b);
	});

	// Compute a combined sum to detect fractional datasets (values in 0..1)
	const combinedSum = Array.from(grouped.values())
		.flat()
		.reduce((s, b) => s + toNumericPercentage(b.percentage), 0);
	const looksFractionalDataset = combinedSum > 0 && combinedSum <= 1.01;
	const scale = looksFractionalDataset ? 100 : 1;

	const items: MergedAudienceAgeItem[] = Array.from(grouped.entries()).map(([ageRange, buckets]) => {
		if (buckets.length >= 2) {
			// Prefer explicit gender labels when present. Sum all male-labeled
			// buckets and female-labeled buckets respectively. This handles
			// messy datasets that might split ranges into multiple fragments.
			const maleBuckets = buckets.filter((b) => ((b as any).gender || "").toLowerCase() === "male");
			const femaleBuckets = buckets.filter((b) => ((b as any).gender || "").toLowerCase() === "female");

			if (maleBuckets.length > 0 || femaleBuckets.length > 0) {
				const maleValue = maleBuckets.reduce((s, bb) => s + toNumericPercentage(bb.percentage), 0) * scale;
				const femaleValue = femaleBuckets.reduce((s, bb) => s + toNumericPercentage(bb.percentage), 0) * scale;
				return {
					ageRange,
					percentage: maleValue + femaleValue,
					malePercentage: maleValue,
					femalePercentage: femaleValue,
				};
			}

			// Fallback: no gender labels. Use first two buckets as male/female
			// when available and aggregate any remaining buckets into the
			// displayed percentage.
			const maleValue = toNumericPercentage(buckets[0].percentage) * scale;
			const femaleValue = buckets[1] ? toNumericPercentage(buckets[1].percentage) * scale : 0;
			const rest = buckets.slice(2).reduce((s, bb) => s + toNumericPercentage(bb.percentage), 0) * scale;
			return {
				ageRange,
				percentage: maleValue + femaleValue + rest,
				malePercentage: maleValue,
				femalePercentage: femaleValue,
			};
		}

		// Single bucket
		const only = buckets[0];
		const pct = toNumericPercentage(only.percentage) * scale;
		return {
			ageRange,
			percentage: pct,
			malePercentage: 0,
			femalePercentage: 0,
		};
	});

	// Sort by numeric start of ageRange when possible
	const sortKey = (s: string) => {
		if (s.toLowerCase().includes("under")) return 0;
		if (s.toLowerCase().includes("over") || s.toLowerCase().includes("plus")) return 100;
		const m = s.match(/(\d+)/);
		return m ? Number(m[1]) : 999;
	};

	return adjustOverflow(items.sort((x, y) => sortKey(x.ageRange) - sortKey(y.ageRange)));
};

// ─── Avatar Extraction ────────────────────────────────────────────────────────

/**
 * Extracts a profile picture URL from a map of social accounts.
 * Prioritizes platforms in this order: TikTok, Instagram, then others.
 */
export const extractAvatarUrl = (socialAccounts?: Record<string, any>): string | undefined => {
	if (!socialAccounts) return undefined;

	// Define priority order
	const priority = ["tiktok", "instagram", "youtube", "facebook"];

	// 1. Try priority platforms first
	for (const platform of priority) {
		const account = socialAccounts[platform] || socialAccounts[platform.toUpperCase()];
		if (account?.avatarUrl) return account.avatarUrl;
	}

	// 2. Fallback to any platform that has an avatar
	for (const account of Object.values(socialAccounts)) {
		if (account?.avatarUrl) return account.avatarUrl;
	}

	return undefined;
};
